// ============================================================================
// File: frontend/src/pages/Dashboard.js
// Version: 128.0 (Phase 9 — развилка по признаку today_dashboard)
// Date: 2025-12-12 02:45:00 EST
// Author: Dashboard Team
// Description: Main dashboard page component with TWO SEPARATE Recent Activity widgets
//              (OfficeUpdatesWidget and AIInsightsWidget) matching Photoshop design.
// 
// Changelog:
// v127.0 (2025-12-12) - 🎨 TWO SEPARATE WIDGETS
//                     - Split RecentActivityWidget into two independent cards
//                     - OfficeUpdatesWidget (top right)
//                     - AIInsightsWidget (bottom right)
//                     - Matches Photoshop design with separate cards
// v121.0 (2025-12-12) - Fixed quick_actions data structure
// v112.0 (2025-12-11) - Added crash protection
// 
// DEPLOYMENT:
// Copy this file to: frontend/src/pages/Dashboard.js
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useBranding } from '../context/BrandingContext';
import apiService from '../services/apiService';
import FitenkoToday from './FitenkoToday';   // quick-260910-iji: экран «Сегодня» арендатора
import TenantToday from './TenantToday';   // Phase 9: экран «Today» арендатора по признаку

// Import widgets
import StatsWidget from '../components/dashboard/StatsWidget';
import QuickActionsWidget from '../components/dashboard/QuickActionsWidget';
import MyTasksWidget from '../components/dashboard/MyTasksWidget';
import OfficeUpdatesWidget from '../components/dashboard/OfficeUpdatesWidget';  // v127: NEW
import AIInsightsWidget from '../components/dashboard/AIInsightsWidget';        // v127: NEW
import ReputationWidget from '../components/dashboard/ReputationWidget';
import ClientCaseStatusWidget from '../components/dashboard/ClientCaseStatusWidget';
import ClientDocumentsWidget from '../components/dashboard/ClientDocumentsWidget';
import WidgetEmbedModal from '../components/dashboard/WidgetEmbedModal'; // [NEW]

import './Dashboard.css';

const DashboardPage = () => {
    const { user, isAuthenticated, loading: authLoading } = useAuth();
    const { isManagingPartner, isAdmin, isClient, isStaff } = usePermissions();
    const { isFitenko, features, featuresError, loading: brandingLoading } = useBranding();   // quick-260910-iji; Phase 9: признаки
    const isRail = isFitenko || features?.shell === 'rail';
    // Phase 9: экран Today выбирается ПРИЗНАКОМ арендатора, а не именем инстанса.
    // Клиент (роль CLIENT) остаётся на прежней клиентской ветке.
    const tenantToday = features.today_dashboard === true && !isClient;

    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [isWidgetModalOpen, setIsWidgetModalOpen] = useState(false); // [FIX] Moved to top level

    // Clock update
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 60000);
        return () => clearInterval(timer);
    }, []);

    const [widgetType, setWidgetType] = useState('chat'); // 'chat' | 'calendar'

    const handleActionClick = (link) => {
        if (link === '#get-widget-code' || link === '#get-widget-code-chat') {
            setWidgetType('chat');
            setIsWidgetModalOpen(true);
        } else if (link === '#get-widget-code-calendar') {
            setWidgetType('calendar');
            setIsWidgetModalOpen(true);
        }
    };

    // Load dashboard data
    useEffect(() => {
        // quick-260910-iji: у fitenko этот экран не рисуется вовсе, и лишний вызов
        // /dashboard/ он делать не должен.
        // Phase 9: до ответа /branding признак неизвестен — не зовём никто; экран Today
        // MLG-агрегатор не зовёт.
        if (authLoading || !isAuthenticated || isFitenko || brandingLoading || tenantToday) return;

        const fetch = async () => {
            setLoading(true);
            try {
                console.log("Fetching Dashboard Data...");
                const data = await apiService.getDashboardData();
                console.log("Dashboard Data Received:", data);
                setDashboardData(data || {});
            } catch (err) {
                console.error("Dashboard Fetch Error:", err);
                setError("System Sync Error");
                setDashboardData({});
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, [authLoading, isAuthenticated, isFitenko, brandingLoading, tenantToday]);

    // 🔴 РАННИЙ ВОЗВРАТ СТОИТ ПОСЛЕ ПОСЛЕДНЕГО ХУКА (quick-260910-iji). Раньше
    //    ставить нельзя: порядок хуков React обязан быть один и тот же на каждой
    //    отрисовке, а признак арендатора приходит асинхронно и меняется.
    //    Ниже хуков нет — только вычисления и разметка.
    if (isFitenko) return <FitenkoToday />;
    // Phase 9: пока /branding не ответил, признак неизвестен — нейтральный загрузчик, а не MLG-дашборд (гонка DEFAULT_BRANDING='mlg').
    if (brandingLoading) return <div className="bg-background text-muted-foreground" style={{ padding: '50px', textAlign: 'center' }}>Loading…</div>;
    // Битый файл признаков — сказать, а не уехать молча на чужой экран.
    // keep: semantic — destructive-red signals an error state, not theme color
    if (featuresError) return <div className="bg-background text-destructive" style={{ padding: '50px', textAlign: 'center' }}>Tenant features unreadable: {featuresError}</div>;
    if (isRail && isAuthenticated && !authLoading) return <FitenkoToday />;
    if (tenantToday && isAuthenticated && !authLoading) return <TenantToday />;

    const getTimeOfDay = () => {
        const h = new Date().getHours();
        return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
    };

    // Simple loader
    if (authLoading || loading) {
        return <div className="bg-background text-muted-foreground" style={{ padding: '50px', textAlign: 'center' }}>Loading System...</div>;
    }

    if (error) {
        // keep: semantic — destructive-red signals an error state, not theme color
        return <div className="bg-background text-destructive" style={{ padding: '50px', textAlign: 'center' }}>{error} <br /><button className="bg-primary text-primary-foreground" onClick={() => window.location.reload()}>Retry</button></div>;
    }

    if (!dashboardData) return null;

    const stats = dashboardData.stats || [];
    const quickActions = dashboardData.quick_actions || [];
    const tasks = dashboardData.my_tasks || [];
    const officeFeed = dashboardData.office_feed || [];
    const aiFeed = dashboardData.ai_feed || [];
    const profiles = dashboardData.profiles || [];

    // [MOD v128] Inject "Get Widget Code" Action
    // We append this action manually to the quickActions list.
    const enhancedQuickActions = [...quickActions];

    // Only add if not already present (to avoid duplication if we re-render)
    if (!enhancedQuickActions.find(a => a.key === 'embed_widget')) {
        enhancedQuickActions.push({
            key: 'embed_widget',
            title: 'Embed Chat Widget',
            main_text: 'Get Chat Code',
            footer_text: 'COPY SCRIPT',
            icon: 'message-circle',
            link: '#get-widget-code-chat', // Special link
            status: 'info'
        });
    }


    // [New] Live Monitor
    if (!enhancedQuickActions.find(a => a.key === 'live_monitor')) {
        enhancedQuickActions.push({
            key: 'live_monitor',
            title: 'Live Monitor',
            main_text: 'Admin Dashboard',
            footer_text: 'VIEW LIVE',
            icon: 'activity', // Or 'monitor' if supported
            link: '/monitor',
            status: 'error' // Red to signify "LIVE" / Alert
        });
    }

    // [New] Embed Calendar Widget Button
    if (!enhancedQuickActions.find(a => a.key === 'embed_calendar')) {
        enhancedQuickActions.push({
            key: 'embed_calendar',
            title: 'Embed Calendar Widget',
            main_text: 'Get Calendar Code',
            footer_text: 'COPY SCRIPT',
            icon: 'calendar',
            link: '#get-widget-code-calendar',
            status: 'success'
        });
    }

    console.log("Dashboard Rendering v127:", {
        stats: stats.length,
        quickActions: quickActions.length,
        tasks: tasks.length,
        officeFeed: officeFeed.length,
        aiFeed: aiFeed.length
    });

    // --- RENDER ---
    // Phase 07.1 Wave 2: bg-background + text-foreground tokenize the page wrapper.
    // On MLG = white/slate-900, on Fitenko = cream/ink via Wave 1 cascade.
    return (
        <div className="reputation-dashboard-page bg-background text-foreground">
            <div className="page-header">
                <div>
                    <h1 className="page-title text-foreground">{getTimeOfDay()}, {user?.first_name || 'Partner'} 👋</h1>
                    <div className="page-subtitle text-muted-foreground">
                        System Overview &bull; {currentTime.toLocaleDateString()} {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                </div>
            </div>

            {isClient ? (
                <div className="dashboard-main-grid bg-card" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                    <ClientCaseStatusWidget data={dashboardData.case_status} />
                    <ClientDocumentsWidget documents={dashboardData.documents} />
                </div>
            ) : (
                <>
                    {/* 1. STATS CARDS (5 cards) */}
                    <StatsWidget stats={stats} />

                    <div className="editor-grid-layout">
                        {/* LEFT COLUMN */}
                        <div className="grid-col main" style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                            {/* QUICK ACTIONS (4 actions) */}
                            {enhancedQuickActions && enhancedQuickActions.length > 0 && (
                                <QuickActionsWidget
                                    actions={enhancedQuickActions}
                                    onActionClick={handleActionClick}
                                />
                            )}

                            {/* REPUTATION WIDGET */}
                            {profiles && profiles.length > 0 && (
                                <ReputationWidget profiles={profiles} />
                            )}

                            {/* MY TASKS */}
                            {tasks && tasks.length > 0 && (
                                <MyTasksWidget tasks={tasks} />
                            )}
                        </div>

                        {/* RIGHT COLUMN - v127: TWO SEPARATE WIDGETS */}
                        <div className="grid-col side" style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1.5rem',
                            height: '100%'
                        }}>
                            {/* OFFICE UPDATES (Top Card) */}
                            {/* OFFICE UPDATES (Top Card) - RESTORED */}
                            {officeFeed && officeFeed.length > 0 && (
                                <OfficeUpdatesWidget events={officeFeed} />
                            )}

                            {/* AI INSIGHTS (Bottom Card) - REMOVED per user request
                            {aiFeed && aiFeed.length > 0 && (
                                <AIInsightsWidget events={aiFeed} />
                            )} 
                            */}
                        </div>
                    </div>
                </>
            )}

            {/* MODALS */}
            <WidgetEmbedModal
                isOpen={isWidgetModalOpen}
                onClose={() => setIsWidgetModalOpen(false)}
                widgetType={widgetType}
            />
        </div>
    );
};

export default DashboardPage;